<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";

import { RouteNames } from "@/router";
import { useAuthStore } from "@/stores/auth";
import DispatchViewAuthenticated from "@/views/dispatch/DispatchViewAuthenticated.vue";

import collect from "@/assets/images/dispatch/collect.png";
import explore from "@/assets/images/dispatch/explore.png";
import skoreHubAvatar from "@/assets/images/dispatch/skore-hub.svg";
import skoreLibAvatar from "@/assets/images/dispatch/skore-lib.svg";
import store from "@/assets/images/dispatch/store.png";
import skoreLogo from "@/assets/images/skore-text.svg";

const authStore = useAuthStore();
const route = useRoute();

const isUserAuthenticated = computed(() => authStore.isAuthenticated);

onMounted(async () => {
  await authStore.ensureUserIsAuthenticated();
});
</script>

<template>
  <div class="dispatch">
    <DispatchViewAuthenticated v-if="isUserAuthenticated" />

    <div v-else class="unauthenticated">
      <header class="border-bottom border-right">
        <div class="logo"><img :src="skoreLogo" alt="skore" /></div>
        <h1 class="text-4xl-medium">Track <em>your</em> Data Science.</h1>
      </header>

      <div class="collect border-bottom border-right">
        <div class="diamond top-left" />
        <div class="diamond bottom-left" />
        <div class="capture" :style="{ backgroundImage: `url(${collect})` }" />
        <div class="blurred-mask"></div>
        <h2 class="text-2xl-medium"><em>Collect</em> and <em>analyze</em> your experiments</h2>
        <p class="text-lg-regular">
          Uncover all the facets of your predictive model: data, metrics, model's internals, etc.
        </p>
      </div>

      <div class="store border-bottom border-right">
        <div class="diamond top-left" />
        <div class="diamond bottom-left" />
        <div class="diamond top-right" />
        <div class="diamond bottom-right" />
        <div class="capture" :style="{ backgroundImage: `url(${store})` }" />
        <div class="blurred-mask"></div>
        <h2 class="text-2xl-medium"><em>Store</em> and <em>retrieve</em> your experiments</h2>
        <p class="text-lg-regular">
          Save all desired data science artifacts produce with skore and find what you search for.
        </p>
      </div>

      <div class="explore border-bottom border-right">
        <div class="diamond top-right" />
        <div class="diamond bottom-right" />
        <div class="capture" :style="{ backgroundImage: `url(${explore})` }" />
        <div class="blurred-mask"></div>
        <h2 class="text-2xl-medium">
          <em>Explore</em>, <em>collaborate</em> and <em>decide</em> your next step
        </h2>
        <p class="text-lg-regular">
          Explore your experiments comparing your best predictive models and make confident
          data-driven decisions with your team.
        </p>
      </div>

      <div class="lib border-bottom border-right">
        <div class="diamond top-right" />
        <div class="diamond bottom-left" />
        <div class="diamond bottom-right" />
        <div class="avatar" :style="{ backgroundImage: `url(${skoreLibAvatar})` }" />
        <h2 class="text-2xl-medium"><i class="icon icon-code"></i> SKORE</h2>
        <h3 class="text-xl-regular">(The Python library)</h3>
        <p class="text-xl-regular">
          Skore is a lightweight open-source Python library that fits perfectly in a data-scientists
          workflow.
        </p>
        <div class="actions">
          <a
            href="https://docs.skore.probabl.ai/0.11/auto_examples/getting_started/plot_skore_getting_started.html"
            target="_blank"
            class="skore-button primary"
          >
            Get started
          </a>
          <a href="https://docs.skore.probabl.ai" target="_blank" class="skore-button ghost">
            Documentation
          </a>
        </div>
      </div>

      <div class="hub border-bottom border-right">
        <div class="avatar" :style="{ backgroundImage: `url(${skoreHubAvatar})` }" />
        <h2 class="text-2xl-medium"><i class="icon icon-window"></i> SKORE HUB</h2>
        <h3 class="text-xl-regular">(The platform)</h3>
        <p class="text-xl-regular">
          Skore Hub is a platform to track, explore, and share your data-science. Interested? Join
          our beta program.
        </p>
        <div class="actions">
          <a
            href="https://probabl.ai/skore#join-waitlist"
            target="_blank"
            class="skore-button primary"
          >
            Join the waitlist
          </a>
          <RouterLink
            class="skore-button ghost"
            :to="{ name: RouteNames.Login, query: { next: route.query.next } }"
          >
            Log in
          </RouterLink>
        </div>
      </div>

      <footer class="border-right">
        <div class="diamond top-right" />
        <p>
          Made with <i class="icon icon-heart" /> by the creators of
          <img
            src="@/assets/images/dispatch/scikit-learn-logo.svg"
            alt="scikit-learn"
            class="scikit-learn-logo"
          />.
        </p>
      </footer>

      <div class="left-margin-1 border-bottom border-right"></div>
      <div class="left-margin-2 border-bottom border-right"></div>
      <div class="left-margin-3 border-bottom border-right"></div>
      <div class="left-margin-4 border-right"></div>
      <div class="right-margin-1 border-bottom"></div>
      <div class="right-margin-2 border-bottom"></div>
      <div class="right-margin-3 border-bottom"></div>
      <div class="right-margin-4"></div>
    </div>
  </div>
</template>

<style scoped>
.dispatch {
  .unauthenticated {
    --margin-width: var(--numbers-32);

    display: grid;
    height: 100dvh;
    background-color: var(--background-secondary);
    grid-template-columns: auto repeat(6, 1fr) auto;
    grid-template-rows: auto auto 1fr auto;

    em {
      color: var(--content-brand);
      font-style: normal;
    }

    .border-bottom {
      position: relative;

      &::after {
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        height: var(--stroke-width-md);
        background-image: repeating-linear-gradient(
          to right,
          var(--color-gray-300) 0,
          var(--color-gray-300) 8px,
          transparent 8px,
          transparent 16px
        );
        content: "";
      }
    }

    .border-right {
      position: relative;

      &::before {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: var(--stroke-width-md);
        background-image: repeating-linear-gradient(
          to bottom,
          var(--color-gray-300) 0,
          var(--color-gray-300) 8px,
          transparent 8px,
          transparent 16px
        );
        content: "";
      }
    }

    .diamond {
      position: absolute;
      z-index: 2;
      width: 8px;
      height: 8px;
      border: 1px solid var(--color-gray-300);
      background-color: var(--background-secondary);
      content: "";
      transform: rotate(135deg);

      &.top-left {
        top: -4.5px;
        left: -4.5px;
      }

      &.bottom-left {
        bottom: -3.5px;
        left: -4.5px;
      }

      &.top-right {
        top: -4.5px;
        right: -3.5px;
      }

      &.bottom-right {
        right: -4.5px;
        bottom: -3.5px;
      }
    }

    header,
    .collect,
    .store,
    .explore,
    .lib,
    .hub,
    footer {
      position: relative;
    }

    .collect,
    .store,
    .explore {
      padding-bottom: var(--numbers-16);
      text-align: center;

      .blurred-mask {
        position: absolute;
        z-index: 2;
        bottom: 0;
        left: 0;
        width: 100%;
        height: calc(100% - 120px);
        background-color: var(--background-secondary);
        filter: blur(25px);
      }

      .capture {
        position: relative;
        z-index: 1;
        height: 225px;
        background-position: center top;
        background-repeat: no-repeat;
        background-size: auto 100%;
      }

      h2,
      p {
        position: relative;
        z-index: 3;
        padding: 0 10%;
      }

      h2 {
        padding-top: 25px;
        margin-top: -80px;
      }

      p {
        color: var(--content-secondary);
      }
    }

    .lib,
    .hub {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--numbers-20);
      gap: var(--numbers-16);
      text-align: center;

      .avatar {
        width: 100%;
        height: 105px;
        background-position: center top;
        background-repeat: no-repeat;
        background-size: auto 100%;
      }

      .icon {
        vertical-align: text-top;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: var(--numbers-8);
      }
    }

    header {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--numbers-20);
      gap: var(--numbers-8);
      grid-column: span 6 / span 6;
      grid-column-start: 2;
      grid-row-start: 1;

      .logo > img {
        width: auto;
        height: var(--numbers-60);
      }
    }

    .collect {
      grid-column: span 2 / span 2;
      grid-column-start: 2;
      grid-row-start: 2;
    }

    .store {
      grid-column: span 2 / span 2;
      grid-column-start: 4;
      grid-row-start: 2;
    }

    .explore {
      grid-column: span 2 / span 2;
      grid-column-start: 6;
      grid-row-start: 2;
    }

    .lib {
      background-color: var(--color-brand-blue-100);
      color: var(--color-brand-blue-500);
      grid-column: span 3 / span 3;
      grid-column-start: 2;
      grid-row-start: 3;

      h3 {
        color: var(--color-brand-blue-400);
      }

      .skore-button.ghost {
        border-color: var(--color-brand-blue-500);
        color: var(--color-brand-blue-500);
      }
    }

    .hub {
      background-color: var(--color-brand-orange-100);
      color: var(--color-brand-orange-500);
      grid-column: span 3 / span 3;
      grid-column-start: 5;
      grid-row-start: 3;

      h3 {
        color: var(--color-brand-orange-400);
      }

      .skore-button.primary {
        background-color: var(--color-brand-orange-500);
      }

      .skore-button.ghost {
        border-color: var(--color-brand-orange-500);
        color: var(--color-brand-orange-500);
      }
    }

    footer {
      padding: var(--numbers-14);
      color: var(--content-secondary);
      grid-column: span 6 / span 6;
      grid-column-start: 2;
      grid-row-start: 4;
      text-align: center;

      .icon-heart {
        color: var(--color-red-500);
        vertical-align: middle;
      }

      .scikit-learn-logo {
        display: inline-block;
        width: auto;
        height: var(--numbers-32);
        vertical-align: text-bottom;
      }
    }

    [class^="left-margin"],
    [class^="right-margin"] {
      width: var(--margin-width);
    }

    .left-margin-1 {
      grid-row-start: 1;
    }

    .left-margin-2 {
      grid-row-start: 2;
    }

    .left-margin-3 {
      grid-row-start: 3;
    }

    .left-margin-4 {
      grid-row-start: 4;
    }

    .right-margin-1 {
      grid-column-start: 8;
      grid-row-start: 1;
    }

    .right-margin-2 {
      grid-column-start: 8;
      grid-row-start: 2;
    }

    .right-margin-3 {
      grid-column-start: 8;
      grid-row-start: 3;
    }

    .right-margin-4 {
      grid-column-start: 8;
      grid-row-start: 4;
    }
  }
}

@media screen and (width >= 1023px) {
  .dispatch {
    .unauthenticated {
      --margin-width: var(--numbers-48);
    }
  }
}

@media screen and (width >= 1279px) {
  .dispatch {
    .unauthenticated {
      --margin-width: var(--numbers-96);
    }
  }
}

@media screen and (width >= 1439px) {
  .dispatch {
    .unauthenticated {
      --margin-width: var(--numbers-128);
    }
  }
}
</style>
